/**
 * BaseAddEntityCommand - Base class for add commands using soft-delete pattern.
 *
 * Uses soft-delete pattern: entities are moved to a hidden layer on undo
 * rather than being permanently deleted, allowing for redo.
 */

import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, findEntityByHandle } from "../core/oda";
import { OpenMode, Visibility } from "../core/constants";
import { UNDO_LAYER_NAME, ensureUndoLayerExists } from "../database/layers";
import { selectionService } from "../selection";
import { recordGraphicsModified } from "../entities";
import { stampCreationMeta } from "../meta/capture";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface BaseAddEntityParams {
  /** Array of entity handles (stable identifiers) */
  entityHandles: string[];
  /** Original layer name for all entities */
  originalLayerName: string;
}

/**
 * Base command for adding entities with undo/redo support.
 */
export abstract class BaseAddEntityCommand implements ICommand {
  protected viewer: DRDrawingViewer;
  protected handles: string[];
  protected originalLayerName: string;
  // Tracks whether this command's own undo() placed entities on the undo layer.
  // False means another command (e.g. ConvertMTextToMLeader) moved them there —
  // in that case dispose() must not erase, as the other command is still responsible.
  private executed = false;
  /** Events oplog CREATED émis par cette création (pour undo/redo). */
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: BaseAddEntityParams) {
    this.viewer = viewer;
    this.handles = params.entityHandles;
    this.originalLayerName = params.originalLayerName;
  }

  /**
   * Handles to stamp with DR_META and emit as oplog CREATED events. Defaults to
   * every tracked handle ; override to narrow to the entity/entities that
   * conceptually represent "the" created object when some handles are mere
   * components of it (e.g. a photo marker's attribute — see
   * AddPhotoMarkerCommand). `this.handles` (undo/redo soft-delete) is untouched
   * either way : every entity must still hide/reappear as a whole.
   */
  protected get metaHandles(): string[] {
    return this.handles;
  }

  /**
   * Execute the command.
   * Entities are already created - this just ensures the undo layer exists.
   */
  execute(): void {
    this.executed = true;
    const db = this.viewer.database;
    if (db) {
      ensureUndoLayerExists(this.viewer.module, db);
    }
    // Capture DR_META (entityUid + createdAt + role) sur les entités fraîchement
    // dessinées. Idempotent et best-effort — n'altère jamais le tracé.
    stampCreationMeta(this.viewer, this.metaHandles);

    // Oplog : un event CREATED par entité DR (entityUid lu juste après le stamp).
    this.oplogEvents = oplogEmitter.emitForHandles(
      this.viewer,
      "CREATED",
      this.metaHandles,
    );
  }

  /**
   * Undo the command.
   * Moves all entities to the hidden undo layer (soft delete).
   */
  undo(): void {
    const entityIds = this.handles
      .map((h) => this.resolveHandle(h))
      .filter((id): id is DrawingModule.OdDbObjectId => id !== null);
    selectionService.removeMany(entityIds);
    for (const id of entityIds) deleteObj(id);
    this.setEntitiesLayer(UNDO_LAYER_NAME, false);

    // Oplog : création annulée → on retire les events CREATED.
    oplogEmitter.revert(this.oplogEvents);

    this.executed = false;
  }

  /**
   * Redo the command.
   * Restores all entities to their original layer.
   */
  redo(): void {
    this.setEntitiesLayer(this.originalLayerName, true);
    selectionService.updateGrid();

    // Oplog : re-création → on ré-ajoute les events CREATED.
    oplogEmitter.reemit(this.oplogEvents);

    this.executed = true;
  }

  /**
   * Dispose the command and clean up.
   * Only erases entities if this command's own undo() placed them on the undo layer.
   * If another command moved the entities there (e.g. ConvertMTextToMLeader), that
   * command retains ownership and is responsible for the final erase.
   */
  dispose(): void {
    // Don't dispose entity if command is executed
    if (this.executed) return;

    for (const handle of this.handles) {
      const entityId = this.resolveHandle(handle);
      if (!entityId) continue;
      const entity = this.openEntity(entityId, OpenMode.kForWrite);
      deleteObj(entityId);
      if (!entity) continue;

      const layerStr = entity.layer();
      const currentLayer = layerStr.toString();
      deleteObj(layerStr);
      if (currentLayer === UNDO_LAYER_NAME) {
        entity.erase(true);
      }
      deleteObj(entity);
    }
  }

  private resolveHandle(handle: string): DrawingModule.OdDbObjectId | null {
    return findEntityByHandle(this.viewer.module, this.viewer.database, handle);
  }

  /**
   * Set the layer and visibility of all entities.
   */
  private setEntitiesLayer(layerName: string, visible: boolean): void {
    for (const handle of this.handles) {
      const entityId = this.resolveHandle(handle);
      if (!entityId) continue;
      this.setEntityVisibility(entityId, visible);
      this.setEntityLayer(entityId, layerName);
      recordGraphicsModified(this.viewer.module, entityId);
      deleteObj(entityId);
    }
  }

  private setEntityVisibility(
    entityId: DrawingModule.OdDbObjectId,
    visible: boolean,
  ): void {
    const entity = this.openEntity(entityId, OpenMode.kForWrite);
    if (!entity) return;
    try {
      entity.setVisibility(
        visible ? Visibility.kVisible : Visibility.kInvisible,
        false,
      );
    } finally {
      deleteObj(entity);
    }
  }

  /**
   * Set the layer of a single entity.
   */
  private setEntityLayer(
    entityId: DrawingModule.OdDbObjectId,
    layerName: string,
  ): void {
    const entity = this.openEntity(entityId, OpenMode.kForWrite);
    if (!entity) return;

    const module = this.viewer.module;
    const layerStr = new module.OdString();
    layerStr.set(layerName);
    entity.setLayer(layerStr, false, true);
    deleteObj(layerStr);
    deleteObj(entity);
  }

  /**
   * Open an entity by ID.
   */
  private openEntity(
    entityId: DrawingModule.OdDbObjectId,
    openMode: DrawingModule.OpenMode,
  ): DrawingModule.OdDbEntity | null {
    if (!entityId || entityId.isNull() || entityId.isErased()) {
      return null;
    }
    return openAs(entityId, this.viewer.module.OdDbEntity, openMode);
  }
}
